/** @format */

import React, {useContext, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';

import {Input, Tab, Tabs, Button, Icon} from '@sttark/design-system';
import {capitalize, formatMoney, formatNumber} from './functions/format';
import UploadFile from './upload_file';

import {QuoteContext} from 'frontier-quoter';
import {useDesignsOrdersHandler} from './hoc/use_designs_orders_handler';
import {useUploadHandler} from '../../utilities/with_upload_handler';
import DuplexFrontBackOption from 'frontier/ds/quoter/duplex_front_back_option';
import UploadedDesignsOrders from 'frontier/ds/quoter/uploaded_designs_orders';
import DesignTotalInput from 'frontier/ds/quoter/design_total_input';
import {DESIGN_UPLOAD_MIMETYPES} from 'frontier-constants';
import Dropzone from 'react-dropzone';

export default function ArtworkUploader(props) {
    const {order, handleChange, ...ctx} = useContext(QuoteContext);

    const wasOrderBlank = useRef(order.is_blank);

    const [isUploadLater, setIsUploadLater] = useState(order.is_blank);

    const {
        designQty,
        totalQty,
        designsOrders,
        fetchDesignsOrders,
        handleDeleteAllDesigns,
        handleDeleteDesign,
        handleUploadComplete,
        handleUploadCompleteAll,
        handleManuallyEnteredQuantities,
        loading
    } = useDesignsOrdersHandler({
        orderId: order.id,
        lastUpdate: ctx.lastUpdate
    });

    const {
        dropping,
        uploading,
        fileProgress,
        handleDragEnter,
        handleDragLeave,
        handleDrop,
        handleClickAddFiles,
        handleCancelUpload,
        handleProcessFile
    } = useUploadHandler({
        saveFileEndpoint: '/account/designs/add/' + order.id,
        onUploadComplete: handleUploadComplete,
        onAllUploadsComplete: handleUploadCompleteAll,
        pickOptions: {
            accept: {$set: DESIGN_UPLOAD_MIMETYPES},
            maxFiles: {$set: 200}
        },
        orderId: order.id
    });

    const hasDesigns = designsOrders.length > 0;

    const handleChangeQuantity = v => {
        handleManuallyEnteredQuantities('totalQty', {target: {value: v}});
    };

    const handleChangeDesignQuantity = v => {
        handleManuallyEnteredQuantities('designQty', {target: {value: v}});
    };

    const handleChangeNotes = v => {
        handleChange({order: {comments_preprinting: {$set: v}}});
    };

    useEffect(() => {
        if (order.is_blank) {
            setIsUploadLater(true);
        }

        if (wasOrderBlank.current === false && order.is_blank) {
            handleDeleteAllDesigns();
        }

        wasOrderBlank.current = order.is_blank;
    }, [order.is_blank]);

    return (
        <>
            <div className="find-out-more-cta">
                <p className="d-md-flex justify-content-end m-0">
                    Need help with your artwork?{' '}
                    <a
                        href="/graphics"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ms-1 my-0">
                        {' '}
                        Find out More
                    </a>
                </p>
            </div>
            <Tabs
                activeTab={isUploadLater ? 'later' : 'now'}
                onChange={v => setIsUploadLater(v === 'later')}>
                <Tab name="now" title="Upload Artwork Now" disabled={order.is_blank}>
                    <>
                        <DuplexFrontBackOption />
                        <Dropzone
                            style={{position: 'static'}}
                            noClick={true}
                            onDragEnter={handleDragEnter}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}>
                            {({getRootProps}) => (
                                <div {...getRootProps()}>
                                    <UploadFile
                                        multiple
                                        dropping={dropping}
                                        numUploaded={designsOrders.length}
                                        numUploading={Object.keys(fileProgress).length}
                                        onClick={handleClickAddFiles}
                                    />
                                </div>
                            )}
                        </Dropzone>
                        {hasDesigns && (
                            <>
                                <h5>Add Design Quantity</h5>
                                <div className="border-y">
                                    <UploadedDesignsOrders
                                        designsOrders={designsOrders}
                                        onDeleteDesign={handleDeleteDesign}
                                        onDesignUpdate={fetchDesignsOrders}
                                        onClickAddFiles={handleClickAddFiles}
                                    />
                                </div>
                                {designsOrders.length % 2 === 0 && (
                                    <Button
                                        secondary
                                        label={`Add Another ${capitalize(
                                            ctx.singularProduct
                                        )} Design`}
                                        onClick={handleClickAddFiles}></Button>
                                )}
                                <QuantityTotal
                                    singularProduct={ctx.singularProduct}
                                    pricePerProduct={
                                        order.quoted_total / order.duplex_aware_total_qty
                                    }
                                    quantity={order.duplex_aware_total_qty}
                                    isLoading={loading || ctx.isSaving}
                                />
                                <div className="mt-5">
                                    <Input
                                        rows={4}
                                        label="Notes about artwork"
                                        value={order.comments_preprinting || ''}
                                        onChange={handleChangeNotes}
                                    />
                                </div>
                            </>
                        )}
                    </>
                </Tab>
                <Tab name="later" title="Upload Artwork Later" disabled={hasDesigns}>
                    <DuplexFrontBackOption />
                    <h5>Add Quantity</h5>
                    <DesignTotalInput
                        designQty={designQty}
                        totalQty={totalQty}
                        onChangeDesignQuantity={handleChangeDesignQuantity}
                        onChangeQuantity={handleChangeQuantity}
                    />
                    <Input
                        rows={4}
                        label="Notes about artwork"
                        value={order.comments_preprinting || ''}
                        onChange={handleChangeNotes}
                    />
                </Tab>
            </Tabs>
        </>
    );
}

function QuantityTotal({quantity, pricePerProduct, singularProduct, isLoading = false}) {
    return (
        <div className="my-4">
            <div style={{position: 'relative'}}>
                <div className="hstack justify-content-between font-label-sm">
                    <span>Quantity Total</span>
                    <span>Price Per {capitalize(singularProduct)}</span>
                </div>
                <div className="hstack justify-content-between font-body-xl">
                    <span>
                        {formatNumber(quantity)}{' '}
                        {isLoading && <span style={{fontSize: '14px'}}>Updating...</span>}
                    </span>
                    <span>{formatMoney(pricePerProduct)}</span>
                </div>
            </div>
        </div>
    );
}
